import React from 'react';
import { Link } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
  IconButton,
  Skeleton
} from '@mui/material';
import {
  Forum as ForumIcon,
  ArrowForward as ArrowIcon,
  Comment as CommentIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const safeAge = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return formatDistanceToNow(d, { addSuffix: true });
  } catch { return ''; }
};

/**
 * Discussions Widget
 * Shows recent family discussions
 */
const DiscussionsWidget = ({ discussions = [], loading = false }) => {
  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={150} height={32} />
        </Box>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="100%" height={20} />
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
        ))}
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ bgcolor: '#388e3c', mr: 2 }}>
            <ForumIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Discussions
          </Typography>
        </Box>
        <IconButton
          component={Link}
          to="/discussions"
          color="primary"
          size="small"
        >
          <ArrowIcon />
        </IconButton>
      </Box>

      {/* Content */}
      {!discussions || discussions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <ForumIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No discussions yet
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            component={Link}
            to="/discussions"
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Start a discussion
          </Typography>
        </Box>
      ) : (
        <List sx={{ p: 0 }}>
          {discussions.slice(0, 5).map((discussion, index) => (
            <ListItem
              key={discussion.id}
              component={Link}
              to="/discussions"
              sx={{
                px: 0,
                py: 1.5,
                borderBottom: index < Math.min(discussions.length, 5) - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
            <ListItemText
              disableTypography
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                      {discussion.title}
                    </Typography>
                    {discussion.replyCount > 0 && (
                      <Chip
                        icon={<CommentIcon sx={{ fontSize: 16 }} />}
                        label={discussion.replyCount}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
              secondary={
                <Box component="div">
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {discussion.content?.substring(0, 80)}
                    {discussion.content?.length > 80 && '...'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    by {discussion.authorName || discussion.createdBy} •{' '}
                    {discussion.createdAt && safeAge(discussion.createdAt)}
                  </Typography>
                </Box>
              }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default DiscussionsWidget;
